import { ArrowLeft } from 'lucide-react';
import { IntelligenceBadge } from '@/components/ui/intelligence';
import AIListingIntake, { type IntakeDraft } from '@/components/listings/AIListingIntake';
import { AIIntakeProvider } from '@/components/listings/ai/AIIntakeProvider';
import { useAIIntake, type HeldFile } from '@/components/listings/ai/aiIntake';
import { Button } from '@/components/ui/button'
import api from '@/lib/axiosInstance'
import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import type { FacilityBenefits } from '@/types/FacilityBenefits'
import ListingTypeDetails, { type ListingTypeDetailsRef } from './application/ListingTypeDetails'
import type { StaticListings } from '@/types/StaticListings'
import UnitDetails, { type UnitDetailsRef } from './application/UnitDetails'
import PropertyDetails, { type PropertyDetailsRef } from './application/PropertyDetails'
import type { UnitFeatures } from '@/types/UnitFeatures'
import { useNavigate, useSearchParams } from 'react-router-dom'
import PriceDetails, { type PriceDetailsRef } from './application/PriceDetails'
import type { PropertySubTypes } from '@/types/PropertySubTypes'
import type { PropertyTypes } from '@/types/PropertyTypes'
import DescriptionDetails, { type DescriptionDetailsRef } from './application/DescriptionDetails'
import AIListingAssistant from '@/components/listings/AIListingAssistant'
import ListingCreateModeChooser, { ListingCreateModeToggle, type ListingCreateMode } from '@/components/listings/ListingCreateMode'
import type { ListingDraft } from '@/lib/ai'
import GalleryDetails, { type GalleryDetailsRef } from './application/GalleryDetails'
import { motion } from 'framer-motion'
import { Skeleton } from '@/components/ui/skeleton'
import type { States } from '@/types/States'
import LocationMappingDetails, { type LocationMappingDetailsRef } from './application/LocationMappingDetails'
import type { Subdistricts } from '@/types/Subdistricts'
import type { Districts } from '@/types/Districts'
import type { PropertyListings } from '@/types/PropertyListings'
import { Spinner } from '@/components/ui/shadcn-io/spinner'
import type { LandTitleTypes } from '@/types/LandTitleTypes'
import { useSidebar } from '@/components/ui/sidebar'

interface OwnerForm {
    firstName: string;
    lastName: string;
    email: string;
    contact: string;
    dob: Date | undefined
}

const ListingAddInner = ({ mode, onModeChange, resetFormForNextDraft }: {
    mode: ListingCreateMode;
    onModeChange: (next: ListingCreateMode) => void;
    /** Remounts the wizard with empty fields, keeping the AI drafts and files. */
    resetFormForNextDraft: () => void;
}) => {
    const intake = useAIIntake();
    const [index, setIndex] = useState<number>(1);
    const [latestIndex, setLatestIndex] = useState<number>(1);
    const navigate = useNavigate();
    const { state: sidebarState, isMobile } = useSidebar();
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);

    const [landTitleTypes, setLandTitleTypes] = useState<LandTitleTypes[]>([]);
    const [districts, setDistricts] = useState<Districts[]>([]);
    const [subdistricts, setSubdistricts] = useState<Subdistricts[]>([]);
    const [selectedDistrict, setSelectedDistrict] = useState<string>('');
    const [selectedSubdistrict, setSelectedSubdistrict] = useState<string>('');
    const [selectedTitleTypeCode, setSelectedTitleTypeCode] = useState<string>('');
    const [jupemNo, setJupemNo] = useState<string>('');
    const [ownershipNumber, setOwnershipNumber] = useState<string>('');
    const [sizeOfLand, setSizeOfLand] = useState<string>('');
    const [lotNo, setLotNo] = useState<string>('');
    const [markAsDuplicate, setMarkAsDuplicate] = useState<boolean>(false);
    const [referenceListing, setReferenceListing] = useState<PropertyListings | null>(null);
    const [proof, setProof] = useState<File | null>(null);
    const [excludeMapPin, setExcludeMapPin] = useState<boolean>(false);
    
    const [leaseTerms, setLeaseTerms] = useState<StaticListings[]>([]);
    const [projectTypes, setProjectTypes] = useState<StaticListings[]>([]);
    const [listingTypes, setListingTypes] = useState<StaticListings[]>([]);
    const [rentalTypes, setRentalTypes] = useState<StaticListings[]>([]);
    const [forSaleOrRentOptions, setForSaleOrRentOptions] = useState<StaticListings[]>([]);
    const [selectedListingType, setSelectedListingType] = useState<number | null>(null);
    const [selectedProjectType, setSelectedProjectType] = useState<number | null>(null);
    const [selectedLeaseTerm, setSelectedLeaseTerm] = useState<number | null>(null);
    const [selectedForSaleOrRent, setSelectedForSaleOrRent] = useState<number | null>(null);
    const [selectedRentalType, setSelectedRentalType] = useState<number | null>(null);
    const [selectedAvailableDate, setSelectedAvailableDate] = useState<Date | undefined>(undefined);
    const [listingReferenceNumber, setListingReferenceNumber] = useState<string>('');
    const [coAgencyListing, setCoAgencyListing] = useState<boolean>(false);
    const [coShareListing, setCoShareListing] = useState<boolean>(false);
    const [forAuction, setForAuction] = useState<boolean>(false);
    const [forTender, setForTender] = useState<boolean>(false);
    const [selectedCoAgencyAgents, setSelectedCoAgencyAgents] = useState<{
        id: number,
        firstName: string,
        lastName: string
    }[]>([]);
    const [selectedCoShareAgents, setSelectedCoShareAgents] = useState<{
        id: number,
        firstName: string,
        lastName: string,
        status: number;
        coShareId: number;
    }[]>([]);

    // Index 2
    const [states, setStates] = useState<States[]>([]);
    const [manualAddress, setManualAddress] = useState<boolean>(false);
    const [rawAddress, setRawAddress] = useState<string>('');
    const [selectedState, setSelectedState] = useState<number | null>(null);
    const [selectedCity, setSelectedCity] = useState<number | null>(null);
    const [selectedTownship, setSelectedTownship] = useState<string>('');
    const [selectedStreet, setSelectedStreet] = useState<string>('');
    const [selectedPostalCode, setSelectedPostalCode] = useState<string>('');
    const [selectedUnitNumber, setSelectedUnitNumber] = useState<string>('');
    const [noTownship, setNoTownship] = useState<boolean>(false);
    const [coord, setCoord] = useState<{
        lat: number | null,
        lng: number | null
    }>({
        lat: null,
        lng: null
    });
    
    // Index 3
    const [propertyTypes, setPropertyTypes] = useState<PropertyTypes[]>([]);
    const [propertyUnitTypes, setPropertyUnitTypes] = useState<StaticListings[]>([]);
    const [propertySubTypes, setPropertySubTypes] = useState<PropertySubTypes[] | null>(null);
    const [titleTypes, setTitleTypes] = useState<StaticListings[]>([]);
    const [directions, setDirections] = useState<StaticListings[]>([]);
    const [zoning, setZoning] = useState<StaticListings[]>([]);
    const [tenures, setTenures] = useState<StaticListings[]>([]);
    const [facilityBenefits, setFacilityBenefits] = useState<FacilityBenefits[] | null>(null);
    const [yearOfCompletion, setYearOfCompletion] = useState<string>('');
    const [selectedDirection, setSelectedDirection] = useState<number | null>(null);
    const [selectedTenure, setSelectedTenure] = useState<number | null>(null);
    const [selectedTitleType, setSelectedTitleType] = useState<number | null>(null);
    const [selectedPropertyType, setSelectedPropertyType] = useState<number | null>(null);
    const [selectedPropertySubType, setSelectedPropertySubType] = useState<number | null>(null);
    const [selectedPropertyUnitType, setSelectedPropertyUnitType] = useState<number | null>(null);
    const [selectedFacilityBenefits, setSelectedFacilityBenefits] = useState<number[]>([]);
    const [selectedLandTypeTitle, setSelectedLandTypeTitle] = useState<number | null>(null);
    const [selectedZoning, setSelectedZoning] = useState<number | null>(null);
    const [isBumiLot, setIsBumiLot] = useState<number | null>(null);
    const [landTypeTitles, setLandTypeTitles] = useState<StaticListings[]>([]);
    const [useTrueLocation, setUseTrueLocation] = useState<boolean>(false);
    const [radius, setRadius] = useState<number| null>(null);
    const [electricityBill, setElectricityBill] = useState<File | string | null>(null);
    const [waterBill, setWaterBill] = useState<File | string | null>(null);

    // Index 4
    const [electricityPhases, setElectricityPhases] = useState<StaticListings[]>([]);
    const [unitFeatures, setUnitFeatures] = useState<UnitFeatures[] | null>(null);
    const [furnishingConditions, setFurnishingConditions] = useState<StaticListings[]>([]);
    const [noOfBathrooms, setNoOfBathrooms] = useState<number | null>(null);
    const [noOfBedrooms, setNoOfBedrooms] = useState<number | null>(null);
    const [noOfMaidRooms, setNoOfMaidRooms] = useState<number | null>(null);
    const [noOfParkingSpots, setNoOfParkingSpots] = useState<number | null>(null);
    const [builtUp, setBuiltUp] = useState<string>('');
    const [selectedBuiltUpUnit, setSelectedBuiltUpUnit] = useState<string>('ft');
    const [floorLoading, setFloorLoading] = useState<string>('');
    const [selectedFloorLoadingUnit, setSelectedFloorLoadingUnit] = useState<string>('tsm');
    const [ceilingHeight, setCeilingHeight] = useState<string>('');
    const [selectedCeilingHeightUnit, setSelectedCeilingHeightUnit] = useState<string>('ft');
    const [floorDetails, setFloorDetails] = useState<{
        block: string;
        floor: string;
        unit: string;
    }>({
        block: '',
        floor: '',
        unit: ''
    });
    const [builtUpDimensions, setBuiltUpDimensions] = useState<{
        width: string;
        length: string;
    }>({
        width: '',
        length: ''
    });
    const [selectedBuiltUpDimensionUnit, setSelectedBuiltUpDimensionUnit] = useState<string>('ft');
    const [landArea, setLandArea] = useState<string>('');
    const [selectedLandAreaUnit, setSelectedLandAreaUnit] = useState<string>('ft');
    const [landAreaDimensions, setLandAreaDimensions] = useState<{
        width: string,
        length: string
    }>({
        width: '',
        length: ''
    });
    const [selectedLandAreaDimensionUnit, setSelectedLandAreaDimensionUnit] = useState<string>('ft');
    const [selectedUnitFeatures, setSelectedUnitFeatures] = useState<number[]>([]);
    const [selectedFurnishingCondition, setSelectedFurnishingCondition] = useState<number | null>(null);
    const [selectedElectricityPhase, setSelectedElectricityPhase] = useState<number | null>(null);
    const [electricitySupply, setElectricitySupply] = useState<number | null>(null);
    const [selectedElectricitySupplyUnit, setSelectedElectricitySupplyUnit] = useState<string>('amp');
    const [totalCargoLifts, setTotalCargoLifts] = useState<number | null>(null);
    const [totalPassengerLifts, setTotalPassengerLifts] = useState<number | null>(null);
    const [maximumLiftCapacity, setMaximumLiftCapacity] = useState<number | null>(null);

    const [builtUpUnits, setBuiltUpUnits] = useState<StaticListings[]>([]);
    const [builtUpDimensionUnits, setBuiltUpDimensionUnits] = useState<StaticListings[]>([]);
    const [landAreaUnits, setLandAreaUnits] = useState<StaticListings[]>([]);
    const [landAreaDimensionUnits, setLandAreaDimensionUnits] = useState<StaticListings[]>([]);
    const [floorLoadingUnits, setFloorLoadingUnits] = useState<StaticListings[]>([]);
    const [electricitySupplyUnits, setElectricitySupplyUnits] = useState<StaticListings[]>([]);
    const [ceilingHeightUnits, setCeilingHeightUnits] = useState<StaticListings[]>([]);

    // Index 5
    const [pricingTypes, setPricingTypes] = useState<StaticListings[]>([]);
    const [selectedPricingType, setSelectedPricingType] = useState<number | null>(null);
    const [price, setPrice] = useState<number | null>(null);
    const [maintenanceFee, setMaintenanceFee] = useState<number | null>(null);

    const [description, setDescription] = useState<string>('');
    const [headline, setHeadline] = useState<string>('');
    // In AI mode the form stays hidden until the agent loads an extracted
    // draft; that hand-off is what reveals it, filled in and ready to check.
    const [aiDraftLoaded, setAiDraftLoaded] = useState(false);
    const [hideBlockNoWhenPublished, setHideBlockNoWhenPublished] = useState<boolean>(false);
    const [ready, setReady] = useState<boolean>(false);
    const [ownerDialog, setOwnerDialog] = useState<boolean>(true);
    const [ownerValidationErrors, setOwnerValidationErrors] = useState<{[key: number]: string[]}>({});
    const [ownerError, setOwnerError] = useState<string | null>(null);
    const ownerErrorText: { [key: string]: string } = {
        'firstName': 'First name is required.',
        'lastName': 'Last name is required.',
        'email': 'Email is required.',
        'email-format': 'Invalid email format.',
        'contact': 'Contact number is required.',
        'contact-format': 'Invalid contact number format.',
        'dob': 'Date of birth is required.',
    };
    const [owners, setOwners] = useState<OwnerForm[]>([{
        firstName: '',
        lastName: '',
        email: '',
        contact: '',
        dob: undefined
    }]);

    const locationMappingDetailsRef = useRef<LocationMappingDetailsRef>(null);
    const listingTypeDetailsRef = useRef<ListingTypeDetailsRef>(null);
    const propertyDetailsRef = useRef<PropertyDetailsRef>(null);
    const unitDetailsRef = useRef<UnitDetailsRef>(null);
    const priceDetailsRef = useRef<PriceDetailsRef>(null);
    const descriptionDetailsRef = useRef<DescriptionDetailsRef>(null);
    const galleryDetailsRef = useRef<GalleryDetailsRef>(null);

    const steps = [
        'Listing Type', 
        'Property Details',
        'Location Mapping',
        'Unit Details', 
        'Price',
        'Description', 
        'Gallery', 
        'Platform posting', 
    ];

    const getListingCriterias = async () => {
        try {
            const response = await api.get(`/listings/criterias`);
            if (!response.data.status) {
                throw new Error(response.data.error);
            }
            setLandTitleTypes(response.data.landTitleTypes);
            setDistricts(response.data.districts);
            setSubdistricts(response.data.subdistricts);
            setZoning(response.data.zoning);
            setProjectTypes(response.data.projectTypes);
            setLeaseTerms(response.data.leaseTerms);
            setStates(response.data.states);
            setPropertyTypes(response.data.propertyTypes);
            setPropertyUnitTypes(response.data.propertyUnitTypes);
            setTitleTypes(response.data.titleTypes);
            setListingTypes(response.data.listingTypes);
            setDirections(response.data.directions);
            setTenures(response.data.tenures);
            setFacilityBenefits(response.data.facilityBenefits);
            setForSaleOrRentOptions(response.data.forSaleOrRentOptions);
            setRentalTypes(response.data.rentalTypes);
            setFurnishingConditions(response.data.furnishingConditions);
            setUnitFeatures(response.data.unitFeatures);
            setElectricityPhases(response.data.electricityPhases);
            setPricingTypes(response.data.pricingTypes);
            setLandTypeTitles(response.data.landTypeTitles);
            setBuiltUpUnits(response.data.builtUpUnits);
            setLandAreaUnits(response.data.landAreaUnits);
            setBuiltUpDimensionUnits(response.data.builtUpDimensionUnits);
            setLandAreaDimensionUnits(response.data.landAreaDimensionUnits);
            setFloorLoadingUnits(response.data.floorLoadingUnits);
            setElectricitySupplyUnits(response.data.electricitySupplyUnits);
            setCeilingHeightUnits(response.data.ceilingHeightUnits);
            setReady(true);
        } catch (err: any) {
            const error = err.response ? err.response.data.error : err.message;
            toast.error(error);
        } finally {
            setIsLoaded(true);
        }
    }

    const getPropertySubTypes = async () => {
        try {
            const response = await api.get(`/listings/properties/types/${selectedPropertyType}`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setPropertySubTypes(response.data.propertySubTypes);
        } catch (err: any) {
            const error = err.response ? err.response.data.error : err.message;
            toast.error(error);
        }
    }

    const handleSubmission = async (e: any) => {
        e.preventDefault();

        try {   
            if(index === 1 && listingTypeDetailsRef.current) {
                const ret = listingTypeDetailsRef.current.callback();
                if(ret) {
                    setIndex(2);
                }
            } else if(index === 2 && propertyDetailsRef.current){
                const ret = propertyDetailsRef.current.callback();
                if(ret) {
                    setIndex(3);
                }
            } else if(index === 3 && locationMappingDetailsRef.current) {
                const ret = await locationMappingDetailsRef.current.callback();
                if(ret) {
                    setIndex(4);
                }
            } else if(index === 4 && unitDetailsRef.current) {
                const ret = unitDetailsRef.current.callback();
                if(ret) {
                    if(forTender) {
                        setIndex(6);
                    } else {
                        setIndex(5);
                    }
                }
            } else if(index === 5 && priceDetailsRef.current) {
                const ret = priceDetailsRef.current.callback();
                if(ret) {
                    setIndex(6);
                }
            } else if(index === 6 && descriptionDetailsRef.current) {
                const ret = descriptionDetailsRef.current.callback();

                if(ret) {
                    setSubmitting(true);
                    var formdata = new FormData();
                    formdata.append('manualAddress', String(manualAddress ? 1: 0));
                    formdata.append('rawAddress', rawAddress ?? '');
                    formdata.append('stateId', String(selectedState ?? ''));
                    formdata.append('cityId', String(selectedCity ?? ''));
                    formdata.append('township', selectedTownship ?? '');
                    formdata.append('street', selectedStreet ?? '');
                    formdata.append('unit', selectedUnitNumber ?? '');
                    formdata.append('postcode', String(selectedPostalCode ?? ''));
                    formdata.append('unitNumber', selectedUnitNumber ?? '');
                    formdata.append('noTownship', String(noTownship ? 1 : 0));
                    formdata.append('useTrueLocation', String(useTrueLocation ? 1 : 0));
                    formdata.append('lat', String(coord?.lat ?? ''));
                    formdata.append('lng', String(coord?.lng ?? ''));
                    formdata.append('radius', String(radius));
                    if(referenceListing) {
                        formdata.append('referenceListingId', String(referenceListing.id));
                    }
                    if(waterBill) {
                        formdata.append('waterBill', waterBill);
                    }
                    if(electricityBill) {
                        formdata.append('electricityBill', electricityBill);
                    }
                    formdata.append('selectedDistrict', selectedDistrict);
                    formdata.append('selectedSubdistrict', selectedSubdistrict);
                    formdata.append('selectedTitleTypeCode', selectedTitleTypeCode);
                    formdata.append('jupemNo', jupemNo);
                    formdata.append('ownershipNumber', ownershipNumber);
                    formdata.append('sizeOfLand', sizeOfLand);
                    formdata.append('lotNo', lotNo);
                    formdata.append('markAsDuplicate', markAsDuplicate ? 'true' : 'false');
                    if(proof) {
                        formdata.append('proof', proof);
                    }

                    formdata.append('listingTypeId', String(selectedListingType ?? ''));
                    formdata.append('projectTypeId', String(selectedProjectType ?? ''));
                    formdata.append('leaseTermId', String(selectedLeaseTerm ?? ''));
                    formdata.append('forSaleOrRentId', String(selectedForSaleOrRent ?? ''));
                    formdata.append('rentalTypeId', String(selectedRentalType ?? ''));
                    formdata.append('availableDate', selectedAvailableDate ? selectedAvailableDate.toISOString() : '');
                    formdata.append('listingReferenceNumber', listingReferenceNumber ?? '');
                    formdata.append('coAgencyListing', String(coAgencyListing ? 1 : 0));
                    formdata.append('coShareListing', String(coShareListing ? 1 : 0));
                    formdata.append('forAuction', String(forAuction ? 1 : 0));
                    formdata.append('forTender', String(forTender ? 1 : 0));
                    formdata.append('coShareAgents', JSON.stringify(selectedCoShareAgents));
                    formdata.append('coAgencyAgents', JSON.stringify(selectedCoAgencyAgents));
            
                    // ✅ Index 3: Property details
                    formdata.append('propertyTypeId', String(selectedPropertyType ?? ''));
                    formdata.append('propertySubTypeId', String(selectedPropertySubType ?? ''));
                    formdata.append('propertyUnitTypeId', String(selectedPropertyUnitType ?? ''));
                    formdata.append('zoningId', String(selectedZoning ?? ''));
                    formdata.append('tenureId', String(selectedTenure ?? ''));
                    formdata.append('titleTypeId', String(selectedTitleType ?? ''));
                    formdata.append('directionId', String(selectedDirection ?? ''));
                    formdata.append('yearOfCompletion', yearOfCompletion ?? '');
                    formdata.append('landTypeTitleId', String(selectedLandTypeTitle ?? ''));
                    formdata.append('isBumiLot', String(isBumiLot));
            
                    selectedFacilityBenefits.forEach((id) => {
                        formdata.append(`facilityBenefits`, String(id));
                    });
            
                    // ✅ Index 4: Unit details
                    formdata.append('noOfBathrooms', String(noOfBathrooms ?? ''));
                    formdata.append('noOfBedrooms', String(noOfBedrooms ?? ''));
                    formdata.append('noOfMaidRooms', String(noOfMaidRooms ?? ''));
                    formdata.append('noOfParkingSpots', String(noOfParkingSpots ?? ''));
                    formdata.append('builtUp', builtUp ?? '');
                    formdata.append('builtUpUnit', selectedBuiltUpUnit ?? '');
                    formdata.append('builtUpWidth', builtUpDimensions.width ?? '');
                    formdata.append('builtUpLength', builtUpDimensions.length ?? '');
                    formdata.append('builtUpDimensionUnit', selectedBuiltUpDimensionUnit ?? '');
                    formdata.append('floorLoading', floorLoading ?? '');
                    formdata.append('floorLoadingUnit', selectedFloorLoadingUnit ?? '');
                    formdata.append('ceilingHeight', ceilingHeight ?? '');
                    formdata.append('ceilingHeightUnit', selectedCeilingHeightUnit ?? '');
                    formdata.append('landArea', landArea ?? '');
                    formdata.append('landAreaUnit', selectedLandAreaUnit ?? '');
                    formdata.append('landAreaWidth', landAreaDimensions.width ?? '');
                    formdata.append('landAreaLength', landAreaDimensions.length ?? '');
                    formdata.append('landAreaDimensionUnit', selectedLandAreaDimensionUnit ?? '');

                    formdata.append('furnishingConditionId', String(selectedFurnishingCondition ?? ''));
                    formdata.append('electricityPhaseId', String(selectedElectricityPhase ?? ''));
                    formdata.append('electricitySupply', String(electricitySupply ?? ''));
                    formdata.append('electricitySupplyUnit', String(selectedElectricitySupplyUnit ?? ''));
                    formdata.append('totalCargoLifts', String(totalCargoLifts ?? ''));
                    formdata.append('totalPassengerLifts', String(totalPassengerLifts ?? ''));
                    formdata.append('maximumLiftCapacity', String(maximumLiftCapacity ?? ''));
                    selectedUnitFeatures.forEach((id) => {
                        formdata.append(`unitFeatures`, String(id));
                    });
                    formdata.append('selectedPricingType', String(selectedPricingType ?? ''));
                    formdata.append('price', String(price ?? ''));
                    formdata.append('maintenanceFee', String(maintenanceFee ?? ''));
                    formdata.append('headline', headline ?? '');
                    formdata.append('description', description ?? '');
                    // The badge on the listing: it began as an AI draft, and
                    // this is the conversation it came from.
                    if (mode === 'ai' && intake.active !== null) {
                        formdata.append('createChannel', 'ai');
                        formdata.append('aiDraftIndex', String(intake.active));
                        const draftId = intake.drafts[intake.active]?.draftId;
                        if (draftId) formdata.append('aiDraftId', draftId);
                        if (intake.session) formdata.append('aiSessionId', intake.session.publicId);
                    }
                    // formdata.append('owners', JSON.stringify(owners));
                    // formdata.append('requireOwnerValidation', String(requireOwnerValidation ? 1 : 0));
                    
                    const response = await api.post(`/listings`, formdata);
                    if(!response.data.status) {
                        throw new Error(response.data.error);
                    }
                    const listingId = response.data.listing.id;

                    // A listing created from an AI draft already has its photos
                    // waiting on this page. Upload them now, while the agent is
                    // still here, rather than sending them to the gallery step
                    // to re-pick files they have already chosen.
                    const draftIndex = intake.active;
                    if (mode === 'ai' && draftIndex !== null) {
                        intake.markCompleted(draftIndex, listingId);
                        const media = response.data.alreadyCreated ? [] : intake.mediaFor(draftIndex);
                        if (media.length) {
                            try {
                                await uploadHeldMedia(listingId, media);
                            } catch (err: unknown) {
                                // The listing exists; only the gallery failed.
                                // Say so instead of losing the listing.
                                const e = err as { response?: { data?: { error?: string } }; message?: string };
                                toast.error(
                                    `Listing created, but its photos did not upload: ${e.response?.data?.error ?? e.message}. Add them on the next screen.`,
                                );
                                navigate(`/listings/edit/${listingId}`);
                                return;
                            }
                        }
                        // More properties from the same upload still to do:
                        // stay here so the remaining drafts and their photos
                        // survive, instead of navigating away and losing them.
                        const remaining = intake.drafts.length - (intake.completed.length + 1);
                        if (remaining > 0) {
                            resetFormForNextDraft();
                            toast.success(
                                `Listing created. ${remaining} more ${remaining === 1 ? 'property' : 'properties'} from this upload still to do.`,
                            );
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                            return;
                        }
                    }
                    navigate(`/listings/edit/${listingId}`);
                }
            }
        } catch (err: any) {
            const error = err.response ? err.response.data.error : err.message;
            toast.error(error);
        } finally {
            setSubmitting(false);
        }
    }

    useEffect(() => {
        getListingCriterias()
    }, [])

    useEffect(() => {
        if(selectedPropertyType) {
            getPropertySubTypes();
        }
    }, [selectedPropertyType]);

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
        if(index > latestIndex) {
            setLatestIndex(index);
        }
    }, [index]);

    useEffect(() => {
        if(selectedListingType && !markAsDuplicate) {
            if(selectedListingType === 3) {
                setSelectedPropertyType(6);
            } else if(selectedListingType === 4) {
                setSelectedPropertyType(7);
            } else if(selectedListingType === 5) {
                setSelectedPropertyType(8);
            } else {
                setSelectedPropertyType(null);
            }
            setSelectedPropertySubType(null);
            setSelectedPropertyUnitType(null);
            setSelectedTenure(null);
            setSelectedTitleType(null);
            setSelectedLandTypeTitle(null);
            setIsBumiLot(null);
            // An AI draft sets the listing type first and the rest here, after
            // the reset above, so its categories are not wiped by it.
            if (aiCategories.current) applyAICategories();
        }
    }, [selectedListingType]);

    useEffect(() => {
        const pending = aiCategories.current?.propertySubTypeId ?? null;
        if (pending && propertySubTypes?.some(x => x.id === pending)) {
            setSelectedPropertySubType(pending);
            aiCategories.current = null;
        } else if(propertySubTypes && propertySubTypes.length === 1) {
            setSelectedPropertySubType(propertySubTypes[0].id);
        }
    }, [propertySubTypes]);

    useEffect(() => {
        if(referenceListing && markAsDuplicate) {
            setJupemNo(referenceListing.jupemNo ?? '');
            setLotNo(referenceListing.lotNo ?? '');
            setSelectedDistrict(referenceListing.districtId ? String(referenceListing.districtId) : '');
            setSelectedSubdistrict(referenceListing.subdistrictId ? String(referenceListing.subdistrictId) : '');
            setSizeOfLand(referenceListing.sizeOfLand ?? '');
            setOwnershipNumber(referenceListing.ownershipNumber ?? '');
            setSelectedTitleTypeCode(referenceListing.titleTypeCode ?? '');

            setSelectedListingType(referenceListing.listingTypeId);
            setSelectedProjectType(referenceListing.projectTypeId);
            setSelectedForSaleOrRent(referenceListing.forSaleOrRentId);
            setSelectedLeaseTerm(referenceListing.leaseTermId ?? null);
            setListingReferenceNumber(referenceListing.listingReferenceNumber ?? '');
            setForAuction(referenceListing.forAuction ? true : false);
            setCoAgencyListing(referenceListing.coAgencyListing ? true : false);
            setCoShareListing(referenceListing.coShareListing ? true : false);
            setSelectedAvailableDate(referenceListing.availableDate ? new Date(referenceListing.availableDate) : undefined);
            setSelectedRentalType(referenceListing.rentalTypeId ?? null);

            // Index 2
            setSelectedState(referenceListing.stateId);
            setSelectedCity(referenceListing.cityId);
            setSelectedTownship(referenceListing.township ?? '');
            setSelectedPostalCode(referenceListing.postcode ?? '');
            setSelectedStreet(referenceListing.street ?? '');
            setSelectedUnitNumber(referenceListing.unit ?? '');
            setRawAddress(referenceListing.rawAddress ?? '');
            setSelectedFacilityBenefits(referenceListing.facilityBenefits);
            // setCoord({
            //     lat: referenceListing.lat != null ? Number(referenceListing.lat) : null,
            //     lng: referenceListing.lng != null ? Number(referenceListing.lng) : null,
            // });

            // Index 3
            setSelectedPropertyType(referenceListing.propertyTypeId);
            setSelectedPropertySubType(referenceListing.propertySubTypeId);
            setSelectedPropertyUnitType(referenceListing.propertyUnitTypeId ?? null);
            setIsBumiLot(referenceListing.bumiLot ?? null);
            setSelectedDirection(referenceListing.directionId);
            setSelectedTenure(referenceListing.tenureId);
            setSelectedZoning(referenceListing.zoningId);
            setSelectedTitleType(referenceListing.titleTypeId);
            setSelectedLandTypeTitle(referenceListing.landTitleTypeId ?? null);
            setSelectedFurnishingCondition(referenceListing.furnishingConditionId);
            setSelectedUnitFeatures(referenceListing.unitFeatures);
            setYearOfCompletion(String(referenceListing.yearOfCompletion) ?? '');

            // Index 4
            setNoOfBathrooms(referenceListing.noOfBathrooms ?? null);
            setNoOfBedrooms(referenceListing.noOfBedrooms ?? null);
            setNoOfMaidRooms(referenceListing.noOfMaidrooms ?? null);

            setBuiltUp(referenceListing.builtUp);
            setBuiltUpDimensions({
                width: referenceListing.builtUpWidth ?? '',
                length: referenceListing.builtUpLength ?? ''
            });
            setFloorLoading(referenceListing.floorLoading ?? '');
            setCeilingHeight(referenceListing.ceilingHeight ?? '');
            setLandArea(referenceListing.landArea ?? '');
            setLandAreaDimensions({
                width: referenceListing.landAreaWidth ?? '',
                length: referenceListing.landAreaLength ?? ''
            });

            setFloorDetails({
                block: referenceListing.unitNumberBlock ?? '',
                floor: referenceListing.unitNumberFloor ?? '',
                unit: referenceListing.unitNumberUnit ?? ''
            });
            setHideBlockNoWhenPublished(referenceListing.hideBlockNoWhenPublished ? true : false)

            setNoOfParkingSpots(referenceListing.noOfParkingSpots ?? null);

            setSelectedElectricityPhase(referenceListing.electricityPhaseId ?? null);
            setElectricitySupply(referenceListing.electricitySupply ?? null);

            setTotalCargoLifts(referenceListing.totalCargoLifts ?? null);
            setTotalPassengerLifts(referenceListing.totalPassengerLifts ?? null);
            setMaximumLiftCapacity(referenceListing.maximumLiftCapacity ?? null);

            setSelectedPricingType(referenceListing.pricingTypeId ?? null);
            setPrice(referenceListing.price);
            setMaintenanceFee(referenceListing.maintenanceFee ?? null);
            
            setHeadline(referenceListing.headline);
            setDescription(referenceListing.description);
        }
    }, [referenceListing]);

    /**
     * Human-readable snapshot of the form for the AI writer. Names are sent
     * rather than IDs so the copy can use the real words, and anything the
     * agent has not filled in stays undefined so it is reported as missing
     * instead of invented.
     */
    const buildAIDraft = (): ListingDraft => {
        const labelOf = (list: StaticListings[], value: number | null) => list.find(x => x.value === value)?.label;
        return {
            propertyType: propertyTypes.find(x => x.id === selectedPropertyType)?.name,
            propertySubType: (propertySubTypes ?? []).find(x => x.id === selectedPropertySubType)?.name,
            purpose: selectedForSaleOrRent === 2 ? 'rent' : selectedForSaleOrRent === 1 ? 'sale' : undefined,
            price: price ?? undefined,
            township: selectedTownship || undefined,
            state: states.find(x => x.id === selectedState)?.name,
            postcode: selectedPostalCode || undefined,
            bedrooms: noOfBedrooms ?? undefined,
            bathrooms: noOfBathrooms ?? undefined,
            builtUp: builtUp || undefined,
            landArea: landArea || undefined,
            tenure: labelOf(tenures, selectedTenure),
            furnishing: labelOf(furnishingConditions, selectedFurnishingCondition),
            yearOfCompletion: yearOfCompletion || undefined,
            facilities: (facilityBenefits ?? []).filter(x => selectedFacilityBenefits.includes(x.id)).map(x => x.name),
            headline: headline || undefined,
            description: description || undefined,
        };
    };

    const applyAICopy = ({ headline: newHeadline, description: newDescription }: { headline?: string; description?: string }) => {
        if (newHeadline !== undefined) setHeadline(newHeadline);
        if (newDescription !== undefined) setDescription(newDescription);
    };

    /**
     * The categories an AI draft resolved, applied once the listing-type
     * effect has run its resets. The sub-type waits for its list to load.
     */
    const aiCategories = useRef<IntakeDraft['category']['ids'] | null>(null);
    const applyAICategories = () => {
        const ids = aiCategories.current;
        if (!ids) return;
        if (ids.propertyTypeId) setSelectedPropertyType(ids.propertyTypeId);
        if (ids.tenureId) setSelectedTenure(ids.tenureId);
        if (ids.titleTypeId) setSelectedTitleType(ids.titleTypeId);
        if (ids.propertyUnitTypeId) setSelectedPropertyUnitType(ids.propertyUnitTypeId);
        if (ids.propertySubTypeId && propertySubTypes?.some(x => x.id === ids.propertySubTypeId)) {
            setSelectedPropertySubType(ids.propertySubTypeId);
            aiCategories.current = null;
        } else if (!ids.propertySubTypeId) {
            aiCategories.current = null;
        }
    };

    const reviewAIDraft = (draft: IntakeDraft, draftIndex: number) => {
        const { fields, category } = draft;
        intake.setActive(draftIndex);
        setHeadline(String(fields.headline ?? ''));
        setDescription(String(fields.description ?? ''));
        setRawAddress(String(fields.rawAddress ?? ''));
        setSelectedTownship(String(fields.township ?? ''));
        setSelectedStreet(String(fields.street ?? ''));
        setSelectedPostalCode(String(fields.postcode ?? ''));
        setOwnershipNumber(String(fields.ownershipNumber ?? ''));
        setLotNo(String(fields.lotNo ?? ''));
        setPrice(typeof fields.price === 'number' ? fields.price : null);
        setNoOfBedrooms(typeof fields.noOfBedrooms === 'number' ? fields.noOfBedrooms : null);
        setNoOfBathrooms(typeof fields.noOfBathrooms === 'number' ? fields.noOfBathrooms : null);
        setBuiltUp(String(fields.builtUp ?? ''));
        setLandArea(String(fields.landArea ?? ''));
        setYearOfCompletion(String(fields.yearOfCompletion ?? ''));
        setJupemNo(String(fields.jupemNo ?? ''));
        setSizeOfLand(String(fields.landAreaStated ?? ''));
        if (typeof fields.titleTypeCode === 'string') setSelectedTitleTypeCode(fields.titleTypeCode);
        if (typeof fields.noOfParkingSpots === 'number') setNoOfParkingSpots(fields.noOfParkingSpots);
        if (typeof fields.noOfMaidrooms === 'number') setNoOfMaidRooms(fields.noOfMaidrooms);
        if (fields.bumiLot === 'Yes') setIsBumiLot(1);
        else if (fields.bumiLot === 'No') setIsBumiLot(0);
        // The choices the review offers alongside the AI's own reading.
        if (typeof fields.unitNumber === 'string') setSelectedUnitNumber(fields.unitNumber);
        if (typeof fields.zoningId === 'number') setSelectedZoning(fields.zoningId);
        const yes = (key: string) => fields[key] === 'Yes';
        if (fields.forAuction) setForAuction(yes('forAuction'));
        if (fields.forTender) setForTender(yes('forTender'));
        if (fields.coAgencyListing) setCoAgencyListing(yes('coAgencyListing'));
        if (fields.coShareListing) setCoShareListing(yes('coShareListing'));

        // Selects the wizard would otherwise make the agent hunt for.
        // What the agent picked in the review wins over what the AI chose.
        const picked = (key: string) => (typeof fields[key] === 'number' ? (fields[key] as number) : null);
        const ids = {
            ...category.ids,
            listingTypeId: picked('listingTypeId') ?? category.ids.listingTypeId,
            propertyTypeId: picked('propertyTypeId') ?? category.ids.propertyTypeId,
            propertySubTypeId: picked('propertySubTypeId') ?? category.ids.propertySubTypeId,
            propertyUnitTypeId: picked('propertyUnitTypeId') ?? category.ids.propertyUnitTypeId,
            tenureId: picked('tenureId') ?? category.ids.tenureId,
            titleTypeId: picked('titleTypeId') ?? category.ids.titleTypeId,
            forSaleOrRentId: picked('forSaleOrRentId') ?? category.ids.forSaleOrRentId,
            projectTypeId: picked('projectTypeId') ?? category.ids.projectTypeId,
            furnishingConditionId: picked('furnishingConditionId') ?? category.ids.furnishingConditionId,
        };
        if (ids.forSaleOrRentId) setSelectedForSaleOrRent(ids.forSaleOrRentId);
        if (ids.projectTypeId) setSelectedProjectType(ids.projectTypeId);
        if (ids.furnishingConditionId) setSelectedFurnishingCondition(ids.furnishingConditionId);
        const rentalTypeId = picked('rentalTypeId');
        if (rentalTypeId) setSelectedRentalType(rentalTypeId);
        // The review resolves the state and city to the form's own IDs
        // (from Google or the agent's pick); otherwise the state is matched
        // by name and the city is left for the agent.
        if (typeof fields.stateId === 'number') {
            setSelectedState(fields.stateId);
            if (typeof fields.cityId === 'number') setSelectedCity(fields.cityId);
        } else {
            const stateName = String(fields.state ?? '').trim().toLowerCase();
            const state = stateName ? states.find(x => x.name.trim().toLowerCase() === stateName) : undefined;
            if (state) setSelectedState(state.id);
        }
        aiCategories.current = ids;
        if (ids.listingTypeId && ids.listingTypeId !== selectedListingType) {
            setSelectedListingType(ids.listingTypeId);
        } else {
            applyAICategories();
        }

        setIndex(1);
        setLatestIndex(1);
        setAiDraftLoaded(true);
        window.scrollTo({ top: 0 });
        toast.info(
            draft.pricing.status === 'estimated'
                ? 'Listing filled in from the AI. The price is only an estimate: set the real asking price, check each step, then submit it for approval.'
                : 'Listing filled in from the AI. Check each step, especially the property type and location, then submit it for approval.',
        );
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    /**
     * Sends the photos and video held for this draft to the listing that was
     * just created. They could not be uploaded earlier: the media endpoint is
     * addressed by listing ID, and until now there was no listing.
     */
    const uploadHeldMedia = async (listingId: number, media: HeldFile[]) => {
        const images = media.filter((m) => m.kind === 'image');
        const videos = media.filter((m) => m.kind === 'video');
        for (const [mediaType, group] of [[1, images], [3, videos]] as const) {
            if (!group.length) continue;
            const form = new FormData();
            form.append('type', String(mediaType));
            group.forEach((m) => form.append('files', m.file));
            const { data } = await api.post(`/listings/${listingId}/medias`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (!data.status) throw new Error(data.error);
        }
    };

    if(!isLoaded) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-8 w-3/4" />
            
                <div className="flex flex-wrap gap-5 mt-5">
                    {
                        [1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                            <Skeleton key={i} className="h-6 w-32" />
                        ))
                    }
                </div>
                <div className="mt-8 space-y-4">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-2/3" />
                    <Skeleton className="h-32 w-full" />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-1/2" />
                </div>
                <div className="flex justify-end gap-2 mt-10">
                    <Skeleton className="h-10 w-24" />
                    <Skeleton className="h-10 w-24" />
                </div>
            </div>
        )
    } 

    // AI mode is the intake panel alone until a draft is loaded. Showing the
    // empty eight-step form beside it would ask the agent to do the very work
    // they came here to hand over.
    const showForm = mode === 'manual' || aiDraftLoaded;

    // On a phone in AI mode the intake's own centred heading is the title,
    // so the header is just the switch, kept to the right.
    const header = (
        <div className='flex flex-wrap items-center justify-between gap-3'>
            <h5 className={`text-sm font-semibold ${mode === 'ai' ? 'hidden lg:block' : ''}`}>Create new listing</h5>
            {mode === 'ai' && <IntelligenceBadge />}
            <div className='ml-auto'>
                <ListingCreateModeToggle mode={mode} onChange={onModeChange} />
            </div>
        </div>
    );

    return ready && (
        <div className='pb-24'>
            {header}
            {/* The chat stays mounted while the form is open: its held photos,
                gallery picks and conversation are what the form is built from. */}
            {mode === 'ai' && (
                <div className={aiDraftLoaded ? 'hidden' : ''}>
                    <AIListingIntake onReview={reviewAIDraft} />
                </div>
            )}
            {mode === 'ai' && aiDraftLoaded && (
                <button
                    type='button'
                    onClick={() => {
                        setAiDraftLoaded(false);
                        window.scrollTo({ top: 0 });
                    }}
                    className='mt-4 flex cursor-pointer items-center gap-1.5 text-sm text-muted-foreground hover:text-black'>
                    <ArrowLeft className='h-4 w-4' /> Back to the AI conversation
                </button>
            )}
            {!showForm ? null : (<>
            <div className="flex flex-wrap gap-5 relative border-gray-200 mt-5">
               {
                    steps.map((item, i) => (
                        <div
                        key={item}
                        onClick={() => {
                            if(i + 1 <= latestIndex) {
                                if(forTender && (i+1) === 5) {
                                    return
                                }
                                setIndex(i + 1);
                            }
                        }}
                        className={`relative z-10 transition-colors cursor-pointer ${
                            index === (i + 1) ? 'text-black font-bold' : 'text-gray-600'
                        }`}>
                        <span className={`relative z-10 uppercase text-sm ${index === (i + 1) ? 'text-black' : 'text-gray-400'}`}>{item}</span>
                        {
                            index === (i + 1) && (
                                <motion.div
                                layoutId="underline"
                                className="absolute left-0 right-0 -bottom-1 h-[2px] bg-black"
                                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                                />
                            )
                        }
                        </div>
                    ))
                }
            </div>
            <form>
                {
                    index === 1 ? (
                        <ListingTypeDetails
                        ref={listingTypeDetailsRef}
                        listingTypes={listingTypes}
                        leaseTerms={leaseTerms}
                        rentalTypes={rentalTypes}
                        projectTypes={projectTypes}
                        forSaleOrRentOptions={forSaleOrRentOptions}
                        selectedListingType={selectedListingType}
                        setSelectedListingType={setSelectedListingType}
                        selectedProjectType={selectedProjectType}
                        setSelectedProjectType={setSelectedProjectType}
                        selectedLeaseTerm={selectedLeaseTerm}
                        setSelectedLeaseTerm={setSelectedLeaseTerm}
                        selectedForSaleOrRent={selectedForSaleOrRent}
                        setSelectedForSaleOrRent={setSelectedForSaleOrRent}
                        selectedRentalType={selectedRentalType}
                        setSelectedRentalType={setSelectedRentalType}
                        selectedAvailableDate={selectedAvailableDate}
                        setSelectedAvailableDate={setSelectedAvailableDate}
                        listingReferenceNumber={listingReferenceNumber}
                        setListingReferenceNumber={setListingReferenceNumber}
                        coAgencyListing={coAgencyListing}
                        setCoAgencyListing={setCoAgencyListing}
                        coShareListing={coShareListing}
                        setCoShareListing={setCoShareListing}
                        forAuction={forAuction}
                        setForAuction={setForAuction} 
                        forTender={forTender}
                        setForTender={setForTender} 
                        selectedCoShareAgents={selectedCoShareAgents}
                        setSelectedCoShareAgents={setSelectedCoShareAgents}
                        selectedCoAgencyAgents={selectedCoAgencyAgents}
                        setSelectedCoAgencyAgents={setSelectedCoAgencyAgents}
                        markAsDuplicate={markAsDuplicate}/>
                    ) : 
                    index === 2 ? (
                        <PropertyDetails 
                        ref={propertyDetailsRef}
                        propertyTypes={propertyTypes.filter(x => x.categoryId === selectedListingType)}
                        propertySubTypes={propertySubTypes}
                        propertyUnitTypes={propertyUnitTypes}
                        tenures={tenures}
                        titleTypes={titleTypes}
                        directions={directions}
                        zoning={zoning}
                        selectedDirection={selectedDirection}
                        setSelectedDirection={setSelectedDirection}
                        selectedTenure={selectedTenure}
                        selectedProjectType={selectedProjectType}
                        setSelectedTenure={setSelectedTenure}
                        selectedTitleType={selectedTitleType}
                        setSelectedTitleType={setSelectedTitleType}
                        selectedPropertyType={selectedPropertyType}
                        setSelectedPropertyType={setSelectedPropertyType}
                        selectedPropertySubType={selectedPropertySubType}
                        setSelectedPropertySubType={setSelectedPropertySubType}
                        selectedPropertyUnitType={selectedPropertyUnitType}
                        setSelectedPropertyUnitType={setSelectedPropertyUnitType} 
                        selectedZoning={selectedZoning}
                        setSelectedZoning={setSelectedZoning}
                        yearOfCompletion={yearOfCompletion}
                        setYearOfCompletion={setYearOfCompletion}
                        facilityBenefits={facilityBenefits}
                        selectedFacilityBenefits={selectedFacilityBenefits}
                        setSelectedFacilityBenefits={setSelectedFacilityBenefits} 
                        selectedLandTypeTitle={selectedLandTypeTitle}
                        setSelectedLandTypeTitle={setSelectedLandTypeTitle}
                        isBumiLot={isBumiLot}
                        setIsBumiLot={setIsBumiLot}
                        landTypeTitles={landTypeTitles}
                        markAsDuplicate={markAsDuplicate}/>
                    ) : 
                    index === 3 ? 
                    (
                        <LocationMappingDetails
                        ref={locationMappingDetailsRef}
                        manualAddress={manualAddress}
                        setManualAddress={setManualAddress}
                        states={states}
                        rawAddress={rawAddress}
                        setRawAddress={setRawAddress}
                        selectedState={selectedState}
                        setSelectedState={setSelectedState}
                        selectedCity={selectedCity}
                        setSelectedCity={setSelectedCity}
                        selectedTownship={selectedTownship}
                        setSelectedTownship={setSelectedTownship}
                        selectedStreet={selectedStreet}
                        setSelectedStreet={setSelectedStreet}
                        selectedPostalCode={selectedPostalCode}
                        setSelectedPostalCode={setSelectedPostalCode}
                        selectedUnitNumber={selectedUnitNumber}
                        setSelectedUnitNumber={setSelectedUnitNumber}
                        noTownship={noTownship}
                        setNoTownship={setNoTownship}
                        coord={coord}
                        setCoord={setCoord} 
                        districts={districts}
                        subdistricts={subdistricts}
                        landTitleTypes={landTitleTypes}
                        selectedDistrict={selectedDistrict}
                        setSelectedDistrict={setSelectedDistrict}
                        selectedSubdistrict={selectedSubdistrict}
                        setSelectedSubdistrict={setSelectedSubdistrict}
                        selectedTitleTypeCode={selectedTitleTypeCode}
                        setSelectedTitleTypeCode={setSelectedTitleTypeCode}
                        jupemNo={jupemNo}
                        setJupemNo={setJupemNo}
                        ownershipNumber={ownershipNumber}
                        setOwnershipNumber={setOwnershipNumber}
                        sizeOfLand={sizeOfLand}
                        setSizeOfLand={setSizeOfLand}
                        lotNo={lotNo}
                        setLotNo={setLotNo}
                        setReferenceListing={setReferenceListing}
                        markAsDuplicate={markAsDuplicate}
                        setMarkAsDuplicate={setMarkAsDuplicate}
                        proof={proof}
                        setProof={setProof}
                        excludeMapPin={excludeMapPin}
                        setExcludeMapPin={setExcludeMapPin}
                        selectedPropertyType={selectedPropertyType}
                        selectedPropertySubType={selectedPropertySubType}
                        radius={radius}
                        setRadius={setRadius}
                        electricityBill={electricityBill}
                        setElectricityBill={setElectricityBill}
                        waterBill={waterBill}
                        setWaterBill={setWaterBill}
                        useTrueLocation={useTrueLocation}
                        setUseTrueLocation={setUseTrueLocation}
                        selectedListingType={selectedListingType}/>
                    )
                    :
                    index === 4 ? (
                        <UnitDetails
                        ref={unitDetailsRef}
                        selectedListingType={selectedListingType}
                        electricityPhases={electricityPhases}
                        unitFeatures={unitFeatures}
                        furnishingConditions={furnishingConditions}
                        noOfBathrooms={noOfBathrooms}
                        setNoOfBathrooms={setNoOfBathrooms}
                        noOfBedrooms={noOfBedrooms}
                        setNoOfBedrooms={setNoOfBedrooms}
                        noOfMaidRooms={noOfMaidRooms}
                        setNoOfMaidRooms={setNoOfMaidRooms}
                        noOfParkingSpots={noOfParkingSpots}
                        setNoOfParkingSpots={setNoOfParkingSpots}
                        floorDetails={floorDetails}
                        setFloorDetails={setFloorDetails}
                        hideBlockNoWhenPublished={hideBlockNoWhenPublished}
                        setHideBlockNoWhenPublished={setHideBlockNoWhenPublished}
                        floorLoading={floorLoading}
                        setFloorLoading={setFloorLoading}
                        selectedFloorLoadingUnit={selectedFloorLoadingUnit}
                        setSelectedFloorLoadingUnit={setSelectedFloorLoadingUnit}
                        ceilingHeight={ceilingHeight}
                        setCeilingHeight={setCeilingHeight}
                        selectedCeilingHeightUnit={selectedCeilingHeightUnit}
                        setSelectedCeilingHeightUnit={setSelectedCeilingHeightUnit}
                        // ✅ Dimensions
                        builtUp={builtUp}
                        setBuiltUp={setBuiltUp}
                        selectedBuiltUpUnit={selectedBuiltUpUnit}
                        setSelectedBuiltUpUnit={setSelectedBuiltUpUnit}
                        builtUpDimensions={builtUpDimensions}
                        setBuiltUpDimensions={setBuiltUpDimensions}
                        selectedBuiltUpDimensionUnit={selectedBuiltUpDimensionUnit}
                        setSelectedBuiltUpDimensionUnit={setSelectedBuiltUpDimensionUnit}
                        landArea={landArea}
                        setLandArea={setLandArea}
                        selectedLandAreaUnit={selectedLandAreaUnit}
                        setSelectedLandAreaUnit={setSelectedLandAreaUnit}
                        landAreaDimensions={landAreaDimensions}
                        setLandAreaDimensions={setLandAreaDimensions}
                        selectedLandAreaDimensionUnit={selectedLandAreaDimensionUnit}
                        setSelectedLandAreaDimensionUnit={setSelectedLandAreaDimensionUnit}
                        // ✅ Features & condition
                        selectedUnitFeatures={selectedUnitFeatures}
                        setSelectedUnitFeatures={setSelectedUnitFeatures}
                        selectedFurnishingCondition={selectedFurnishingCondition}
                        setSelectedFurnishingCondition={setSelectedFurnishingCondition}
                        selectedElectricityPhase={selectedElectricityPhase}
                        setSelectedElectricityPhase={setSelectedElectricityPhase}
                        electricitySupply={electricitySupply}
                        setElectricitySupply={setElectricitySupply}
                        selectedElectricitySupplyUnit={selectedElectricitySupplyUnit}
                        setSelectedElectricitySupplyUnit={setSelectedElectricitySupplyUnit}

                        totalCargoLifts={totalCargoLifts}
                        setTotalCargoLifts={setTotalCargoLifts}
                        totalPassengerLifts={totalPassengerLifts}
                        setTotalPassengerLifts={setTotalPassengerLifts}
                        maximumLiftCapacity={maximumLiftCapacity}
                        setMaximumLiftCapacity={setMaximumLiftCapacity}

                        selectedPropertyType={selectedPropertyType}
                        selectedPropertySubType={selectedPropertySubType}
                        markAsDuplicate={markAsDuplicate}
                        
                        builtUpUnits={builtUpUnits}
                        landAreaUnits={landAreaUnits}
                        builtUpDimensionUnits={builtUpDimensionUnits}
                        landAreaDimensionUnits={landAreaDimensionUnits}
                        floorLoadingUnits={floorLoadingUnits}
                        electricitySupplyUnits={electricitySupplyUnits}
                        ceilingHeightUnits={ceilingHeightUnits}/>
                    )
                    : index === 5 ? (
                        <PriceDetails 
                        ref={priceDetailsRef}
                        selectedForSaleOrRent={selectedForSaleOrRent}
                        pricingTypes={pricingTypes}
                        selectedPricingType={selectedPricingType}
                        setSelectedPricingType={setSelectedPricingType}
                        price={price}
                        setPrice={setPrice}
                        maintenanceFee={maintenanceFee}
                        setMaintenanceFee={setMaintenanceFee}
                        builtUp={builtUp}
                        landArea={landArea}
                        markAsDuplicate={markAsDuplicate}
                        forTender={forTender}/>
                    )
                    : index === 6 ? (
                        <DescriptionDetails 
                        ref={descriptionDetailsRef}
                        description={description}
                        setDescription={setDescription}
                        headline={headline}
                        setHeadline={setHeadline}
                        markAsDuplicate={markAsDuplicate}
                        aiSlot={
                            <AIListingAssistant
                                mode='generate'
                                getDraft={buildAIDraft}
                                onAccept={applyAICopy} />
                        }/>
                    ) : 
                    index === 7 && (
                        <GalleryDetails 
                        ref={galleryDetailsRef}
                        markAsDuplicate={markAsDuplicate}/>
                    )
                }
                <div
                className='fixed bottom-0 right-0 z-30 border-t border-gray-200 bg-white/95 backdrop-blur transition-[left] duration-200 ease-linear'
                style={{ left: !isMobile && sidebarState === 'expanded' ? 'var(--sidebar-width)' : 0 }}>
                    <div className='mx-auto flex w-full max-w-7xl justify-end px-4 py-3 sm:px-6 lg:px-8'>
                        <Button
                        className='mr-1'
                        variant={'secondary'}
                        onClick={(e) => {
                            e.preventDefault();
                            if(index === 1) {
                                navigate('/listings?page=1&length=10')
                            } else if(index > 1) {
                                setIndex((prev) => (prev > 1 ? prev - 1 : 1));
                            }
                        }}>BACK</Button>
                        <Button onClick={handleSubmission} disabled={submitting}>
                            {
                                submitting && (
                                     <Spinner/>
                                )
                            }
                            NEXT
                        </Button>
                    </div>
                </div>
                {/* <Dialog open={ownerDialog} onOpenChange={setOwnerDialog}>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
                        <DialogHeader>
                            <DialogTitle>Owner Details</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-6 mt-4 overflow-y-auto max-h-[calc(90vh-120px)] px-1">
                            {ownerError && (
                                <Alert variant="destructive" className="bg-red-50 border-red-200">
                                    <AlertDescription>{ownerError}</AlertDescription>
                                </Alert>
                            )}
                            
                            {owners.map((owner, ownerIndex) => (
                                <motion.div
                                    key={ownerIndex}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="border rounded-lg p-6 bg-gray-50 relative">
                                    
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="font-semibold text-gray-700">
                                            Owner {ownerIndex + 1}
                                        </h3>
                                        {owners.length > 1 && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleRemoveOwner(ownerIndex)}
                                                className="text-red-600 hover:text-red-700 hover:bg-red-50">
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <LabelWithAsterisk label="First Name" className="mb-2" />
                                            <Input
                                                type="text"
                                                placeholder="John"
                                                value={owner.firstName}
                                                onChange={(e) => handleOwnerChange(ownerIndex, 'firstName', e.target.value)}
                                                className={ownerValidationErrors[ownerIndex]?.includes('firstName') ? 'border-red-400' : ''}
                                            />
                                            <span className="text-red-500 text-xs">
                                                {getOwnerError(ownerIndex, 'firstName')}
                                            </span>
                                        </div>
                                        
                                        <div>
                                            <LabelWithAsterisk label="Last Name" className="mb-2" />
                                            <Input
                                                type="text"
                                                placeholder="Doe"
                                                value={owner.lastName}
                                                onChange={(e) => handleOwnerChange(ownerIndex, 'lastName', e.target.value)}
                                                className={ownerValidationErrors[ownerIndex]?.includes('lastName') ? 'border-red-400' : ''}
                                            />
                                            <span className="text-red-500 text-xs">
                                                {getOwnerError(ownerIndex, 'lastName')}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mt-4">
                                        <LabelWithAsterisk label="Email" className="mb-2" />
                                        <Input
                                            type="email"
                                            placeholder="owner@example.com"
                                            value={owner.email}
                                            onChange={(e) => handleOwnerChange(ownerIndex, 'email', e.target.value)}
                                            className={
                                                ownerValidationErrors[ownerIndex]?.includes('email') || 
                                                ownerValidationErrors[ownerIndex]?.includes('email-format')
                                                    ? 'border-red-400' : ''
                                            }
                                        />
                                        <span className="text-red-500 text-xs">
                                            {getOwnerError(ownerIndex, 'email') || getOwnerError(ownerIndex, 'email-format')}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 mt-4">
                                        <div>
                                            <LabelWithAsterisk label="Contact" className="mb-2" />
                                            <Input
                                                type="tel"
                                                placeholder="0123456789"
                                                value={owner.contact}
                                                onChange={(e) => handleOwnerChange(ownerIndex, 'contact', e.target.value)}
                                                className={
                                                    ownerValidationErrors[ownerIndex]?.includes('contact') || 
                                                    ownerValidationErrors[ownerIndex]?.includes('contact-format')
                                                        ? 'border-red-400' : ''
                                                }
                                            />
                                            <span className="text-red-500 text-xs">
                                                {getOwnerError(ownerIndex, 'contact') || getOwnerError(ownerIndex, 'contact-format')}
                                            </span>
                                        </div>
                                        
                                        <div>
                                            <LabelWithAsterisk label="Date of Birth" className="mb-2" />
                                            <DatePicker
                                                value={owner.dob}
                                                callback={(date) => {
                                                    if (date instanceof Date) handleOwnerChange(ownerIndex, 'dob', date);
                                                }}
                                                className={`w-full ${ownerValidationErrors[ownerIndex]?.includes('dob') ? 'border-red-400' : ''}`}
                                            />
                                            <span className="text-red-500 text-xs">
                                                {getOwnerError(ownerIndex, 'dob')}
                                            </span>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleAddOwner}
                                className="w-full border-dashed border-2">
                                <Plus className="w-4 h-4 mr-2" />
                                Add
                            </Button>

                            <div className="flex justify-end gap-2 pt-4 sticky bottom-0 bg-white pb-2">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setOwnerDialog(false);
                                        setOwnerError(null);
                                        setOwnerValidationErrors({});
                                    }}
                                    size="sm">
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleOwnerSubmission}
                                    className="signature-btn"
                                    size="sm">
                                    Submit
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog> */}
            </form>
            </>)}
        </div>
    )
}

/**
 * Owns the creation method and forces a clean slate when it changes.
 *
 * The method lives in the URL, and the form is keyed on it, so switching
 * remounts the whole thing: every field, step and upload starts empty. That
 * is deliberate — half a manually typed listing merged into an AI extraction
 * is worse than starting again, and the two paths collect data differently.
 *
 * This only ever runs before a listing exists. Once it is created the agent
 * is on the edit page, which has an ID and no notion of a creation method.
 */
const ListingAdd = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [epoch, setEpoch] = useState(0);
    const raw = searchParams.get('mode');
    const mode: ListingCreateMode | null = raw === 'ai' || raw === 'manual' ? raw : null;

    if (!mode) {
        return (
            <ListingCreateModeChooser
                onSelect={(next) => setSearchParams({ mode: next }, { replace: true })} />
        );
    }

    // The AI state lives outside the keyed form on purpose. Creating one
    // listing from a batch remounts the wizard to clear every field, and the
    // remaining drafts and their held photos have to survive that.
    return (
        <AIIntakeProvider key={mode}>
            <ListingAddInner
                key={`${mode}-${epoch}`}
                mode={mode}
                onModeChange={(next) => {
                    setEpoch(0);
                    setSearchParams({ mode: next }, { replace: true });
                }}
                resetFormForNextDraft={() => setEpoch((n) => n + 1)} />
        </AIIntakeProvider>
    );
}

export default ListingAdd
